import React, { useState, useEffect, useCallback } from 'react';
import { getTasks, updateTask, getTeams, getBrands } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { DotsSix, ArrowRight, Clock, User, UsersThree } from '@phosphor-icons/react';

const STATUSES = ['New Idea Repository', 'Backlog', 'In Discussion', 'In Design', 'In Development', 'In QA', 'Blocked', 'Completed', 'Implemented'];
const STATUS_COLORS = {
  'New Idea Repository': '#C4B5FD', 'Backlog': '#E8E8E8', 'In Discussion': '#FFE7B3', 'In Design': '#D8B4E2',
  'In Development': '#B3D4FF', 'In QA': '#FFC2B3', 'Blocked': '#FF3B30',
  'Completed': '#34C759', 'Implemented': '#10B981',
};
const PRIORITY_COLORS = { High: '#FF3B30', Medium: '#FFB800', Low: '#34C759' };

export default function KanbanPage() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [brands, setBrands] = useState([]);
  const [filterTeam, setFilterTeam] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [draggedTask, setDraggedTask] = useState(null);
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    try {
      const params = {};
      if (filterTeam) params.assigned_team = filterTeam;
      if (filterBrand) params.brand_id = filterBrand;
      const [tasksRes, teamsRes, brandsRes] = await Promise.all([
        getTasks(params), getTeams(), getBrands()
      ]);
      setTasks(tasksRes.data);
      setTeams(teamsRes.data);
      setBrands(brandsRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filterTeam, filterBrand]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDrop = async (status) => {
    if (!draggedTask || draggedTask.status === status) {
      setDraggedTask(null);
      return;
    }
    try {
      await updateTask(draggedTask.id, { status });
      setTasks(prev => prev.map(t => t.id === draggedTask.id ? { ...t, status } : t));
    } catch (e) { console.error(e); }
    setDraggedTask(null);
  };

  const teamMap = Object.fromEntries(teams.map(t => [t.id, t.name]));

  if (loading) return <div className="p-8 text-[#555555]" data-testid="kanban-loading">Loading board...</div>;

  return (
    <div className="flex flex-col h-full" data-testid="kanban-page">
      {/* Header */}
      <div className="px-6 py-4 border-b border-[#E8E8E8] flex items-center justify-between gap-4 flex-wrap shrink-0">
        <h1 className="font-['Manrope'] text-xl font-bold tracking-tight text-[#0F0F0F]">Kanban Board</h1>
        <div className="flex items-center gap-3">
          <select
            data-testid="kanban-filter-team"
            value={filterTeam}
            onChange={e => setFilterTeam(e.target.value)}
            className="h-8 text-xs border border-[#E8E8E8] rounded-md px-2 bg-white text-[#0F0F0F] focus:ring-1 focus:ring-[#32B4AC]"
          >
            <option value="">All Teams</option>
            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <select
            data-testid="kanban-filter-brand"
            value={filterBrand}
            onChange={e => setFilterBrand(e.target.value)}
            className="h-8 text-xs border border-[#E8E8E8] rounded-md px-2 bg-white text-[#0F0F0F] focus:ring-1 focus:ring-[#32B4AC]"
          >
            <option value="">All Brands</option>
            {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
      </div>

      {/* Board */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden p-4">
        <div className="flex gap-4 h-full" style={{ minWidth: `${STATUSES.length * 304}px` }}>
          {STATUSES.map(status => {
            const now = new Date();
            const columnTasks = tasks.filter(t => {
              // Deadline override: if overdue and not completed/implemented, display in Backlog
              const isOverdue = t.deadline && !['Completed', 'Implemented'].includes(t.status) && new Date(t.deadline) < now;
              const displayStatus = isOverdue ? 'Backlog' : t.status;
              return displayStatus === status;
            });
            return (
              <div
                key={status}
                className="w-[280px] shrink-0 flex flex-col bg-[#F4F4F4] rounded-md"
                data-testid={`kanban-column-${status.replace(/\s/g, '-').toLowerCase()}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDrop(status)}
              >
                {/* Column header */}
                <div className="flex items-center gap-2 px-3 py-2.5 border-b border-[#E8E8E8]">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: STATUS_COLORS[status] }} />
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#555555]">{status}</span>
                  <span className="ml-auto text-xs font-semibold text-[#555555] bg-white px-1.5 py-0.5 rounded-full">{columnTasks.length}</span>
                </div>

                {/* Cards */}
                <div className="flex-1 overflow-y-auto p-2 space-y-2">
                  {columnTasks.map(task => (
                    <div
                      key={task.id}
                      draggable
                      onDragStart={() => setDraggedTask(task)}
                      onClick={() => navigate(`/tasks/${task.id}`)}
                      className="bg-white border border-[#E8E8E8] rounded-md p-3 cursor-pointer hover:border-[#1A1A1A] transition-colors duration-150 group"
                      data-testid={`kanban-task-${task.id}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h4 className="text-sm font-medium text-[#0F0F0F] leading-snug line-clamp-2">{task.title}</h4>
                        <DotsSix size={14} className="text-[#E8E8E8] shrink-0 group-hover:text-[#555555] transition-colors cursor-grab" />
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: PRIORITY_COLORS[task.priority] + '18', color: PRIORITY_COLORS[task.priority] }}>
                          {task.priority}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#E8E8E8] text-[#1A1A1A] font-medium">{task.task_type}</span>
                      </div>
                      {/* Team & Owner */}
                      <div className="mt-2.5 pt-2 border-t border-[#F4F4F4] space-y-1.5">
                        <div className="flex items-center gap-1.5" data-testid={`kanban-task-team-${task.id}`}>
                          <UsersThree size={12} className="text-[#32B4AC] shrink-0" />
                          <span className="text-[10px] font-medium text-[#1A1A1A] truncate">{teamMap[task.assigned_team] || 'No team'}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5" data-testid={`kanban-task-owner-${task.id}`}>
                            <User size={12} className="text-[#555555] shrink-0" />
                            <span className="text-[10px] text-[#555555] truncate max-w-[120px]">{task.current_owner_name || 'Unassigned'}</span>
                          </div>
                          {task.deadline && (
                            <div className="flex items-center gap-1 text-[#555555]">
                              <Clock size={10} />
                              <span className="text-[10px]">{new Date(task.deadline).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {columnTasks.length === 0 && (
                    <div className="text-center py-8 text-xs text-[#555555]">No tasks</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
